// The Firmware fact on an access point's page, which is also a way into the firmware act (#1889,
// Gabriel Provencher's review of the 2026-10-20 mockup board, concept 2). The build the device
// reports is a button, and its menu holds three entries:
//
//  - *Upgrade to <build>*, where the server suggests one (`GET /devices/{serial}/firmware-request-options`'s
//    `suggestion`, read when the menu opens and only then);
//  - *Choose another build…*;
//  - *Delivery history*, the page's own `GET /devices/{serial}/firmware-deliveries` read, listed in the
//    menu with each delivery's headline.
//
// **The first two open the same Firmware card the Actions menu's *Upgrade firmware…* opens**, and
// nothing else: §18 makes the firmware request *"the one firmware control on the head"*, and
// ADR-0413 and #1841 make the card its one place. The card preselects the server's suggestion
// (ADR-0404 `2.1d`), so *Upgrade to* lands on the build it names and the person's click there is still
// the approval. Nothing here sends anything.
//
// Restates ADR-0367 decision 2.1a — the upgrade entries are disabled for a device that is not online
// and for one the controller holds no record of, with the reason the act gives, as the Actions entry is.
import { useRef, useState } from "react";
import { api } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import type { components } from "../../api/schema";
import { Popover, When } from "../../ui";
import { absentExplain, isAbsent, notLiveExplain, type Device, type FirmwareDelivery } from "./commands";
import { DeliveryHeadline } from "./delivery";
import { Glyph, OpenIcon } from "../../icons";

type Options = components["schemas"]["DeviceFirmwareRequestOptions"];

export default function FirmwareFact({ device, mayUpgrade, busy, onUpgrade, deliveries }: {
  device: Device;
  /** The caller may command this device, so the upgrade entries are offered. */
  mayUpgrade: boolean;
  /** The page's write lane is busy (#189). */
  busy: boolean;
  /** Opens the page's Firmware card in its act. */
  onUpgrade(): void;
  /** The page's read of this device's deliveries, newest first; `unread` where it failed, absent while it is out. */
  deliveries?: FirmwareDelivery[] | "unread";
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(false);
  const blocked = isAbsent(device) ? absentExplain(device) : !device.online ? notLiveExplain(device, "a firmware upgrade") : undefined;
  const ask = open && mayUpgrade && !blocked;
  const options = useQuery<Options>(() => ask
    ? api.GET("/devices/{serial}/firmware-request-options", { params: { path: { serial: device.serial } } })
    : SKIP, [device.serial, ask]);
  const data = options.status === "ready" ? options.data : undefined;
  const artifact = data?.suggestion.state === "suggested" ? data.suggestion.artifact : undefined;
  const suggested = data?.entries.find((e) => e.artifact === artifact && e.qualified && !!e.predicted_firmware);
  const close = () => { setOpen(false); setHistory(false); };
  const upgrade = () => { close(); anchor.current?.focus(); onUpgrade(); };
  return (
    <>
      <button ref={anchor} type="button" className="fact-menu" aria-haspopup="true" aria-expanded={open} onClick={() => { setHistory(false); setOpen((o) => !o); }}>
        {device.firmware ? <code>{device.firmware}</code> : <span className="muted">—</span>}<Glyph icon={OpenIcon} size={12} className="seg-caret" />
      </button>
      <Popover open={open} anchor={anchor} onClose={close} label={`Firmware of ${device.name}`}>
        {history
          ? (
            <div className="firmware-history">
              {deliveries === "unread" && <p className="muted">The deliveries could not be read.</p>}
              {deliveries === undefined && <p className="muted">Reading the deliveries…</p>}
              {Array.isArray(deliveries) && deliveries.length === 0 && <p className="muted">None recorded.</p>}
              {Array.isArray(deliveries) && deliveries.length > 0 && (
                <ul aria-label="Delivery history">
                  {deliveries.map((d) => <li key={d.id}><When value={d.created_at} /><DeliveryHeadline d={d} /></li>)}
                </ul>
              )}
            </div>
          )
          : (
            <div className="act-menu">
              {mayUpgrade && suggested && (
                <button type="button" className="btn btn-quiet" disabled={busy || !!blocked} title={blocked} onClick={upgrade}>
                  Upgrade to {suggested.predicted_firmware}
                </button>
              )}
              {mayUpgrade && (
                <button type="button" className="btn btn-quiet" disabled={busy || !!blocked} title={blocked} onClick={upgrade}>
                  {suggested ? "Choose another build…" : "Choose a build…"}
                </button>
              )}
              {mayUpgrade && <hr className="act-divider" />}
              <button type="button" className="btn btn-quiet" onClick={() => setHistory(true)}>Delivery history</button>
            </div>
          )}
      </Popover>
    </>
  );
}
